import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Alert,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import { ArrowBack, Delete, People, FolderOpen, ContentCopy } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface Organization {
  id: string;
  name: string;
  invite_code: string;
  subscription_tier: string;
  user_count: number;
  project_count: number;
  created_at: string;
}

const ManageOrganizationsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [confirmText, setConfirmText] = useState('');

  // Fetch organizations function
  const fetchOrganizations = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/auth/superuser/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrganizations(response.data.organizations);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  // Call useEffect before any returns
  useEffect(() => {
    if (user && user.role === 'superuser') {
      fetchOrganizations();
    }
  }, [user]);

  // Early return AFTER all hooks
  if (!user || user.role !== 'superuser') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Only superusers can manage organizations.
        </Alert>
        <Button onClick={() => navigate('/projects')} sx={{ mt: 2 }}>
          Back to Projects
        </Button>
      </Container>
    );
  }

  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;

    if (confirmText !== selectedOrg.name) {
      setError('Organization name does not match');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/auth/superuser/organizations/${selectedOrg.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(`Organization "${selectedOrg.name}" deleted successfully`);
      setDeleteDialog(false);
      setSelectedOrg(null);
      setConfirmText('');
      fetchOrganizations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete organization');
      setDeleteDialog(false);
    }
  };

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setSuccess('Invite code copied to clipboard');
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/projects')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>
            Manage Organizations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Superuser only - View and manage all organizations
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => navigate('/superuser/create-organization')}
        >
          Create Organization
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Organization</strong></TableCell>
                <TableCell><strong>Invite Code</strong></TableCell>
                <TableCell align="center"><strong>Users</strong></TableCell>
                <TableCell align="center"><strong>Projects</strong></TableCell>
                <TableCell><strong>Tier</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight={600}>
                      {org.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {org.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <code style={{ fontSize: '0.9em', fontWeight: 600 }}>
                        {org.invite_code}
                      </code>
                      <IconButton
                        size="small"
                        onClick={() => copyInviteCode(org.invite_code)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<People />}
                      label={org.user_count}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<FolderOpen />}
                      label={org.project_count}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={org.subscription_tier}
                      size="small"
                      color={
                        org.subscription_tier === 'enterprise'
                          ? 'primary'
                          : org.subscription_tier === 'pro'
                          ? 'secondary'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(org.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setSelectedOrg(org);
                        setDeleteDialog(true);
                        setConfirmText('');
                      }}
                      disabled={org.id === user?.organization_id}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <Delete />
            Delete Organization
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>WARNING:</strong> This will permanently delete:
          </DialogContentText>
          <Box component="ul" sx={{ color: 'text.secondary', mb: 2 }}>
            <li>All users in "{selectedOrg?.name}"</li>
            <li>All projects ({selectedOrg?.project_count} projects)</li>
            <li>All uploaded documents</li>
            <li>All requirements and compliance results</li>
          </Box>
          <DialogContentText sx={{ mb: 2, color: 'error.main', fontWeight: 600 }}>
            This action cannot be undone!
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            To confirm, type the organization name: <strong>{selectedOrg?.name}</strong>
          </DialogContentText>
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Enter organization name"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteOrg}
            color="error"
            variant="contained"
            disabled={confirmText !== selectedOrg?.name}
          >
            Delete Organization
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageOrganizationsPage;
